import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { money, num, pct, valuePct } from "@owners/format";
import { api, usePrefs } from "../api";
import { HotelPicker, MoneyVar, PtsVar, hotelParam } from "../components/Numbers";

/** Flash Reports (SPEC §3): occupancy, ADR, RevPAR, rooms sold and revenue for one or many hotels. */

type Period = "yesterday" | "last_week" | "last_month" | "ytd" | "t12";
const PERIODS: [Period, string][] = [["yesterday", "Yesterday"], ["last_week", "Last week"], ["last_month", "Last month"], ["ytd", "Year to date"], ["t12", "Trailing 12 months"]];

interface M { occupancy: number | null; adr: number | null; revpar: number | null; roomsSold: number; revenue: number; days: number }
interface FlashRes {
  label: string; from: string; to: string; days: number;
  hotels: Array<{ id: string; name: string; dataThrough: string | null; daysReported: number; current: M | null; lastYear: M | null; budget?: M | null }>;
  total: { hotels: number; current: M; lastYear: M | null; budget?: M | null } | null;
}

export function FlashReports() {
  const prefs = usePrefs().data;
  const [picked, setPeriod] = useState<Period | null>(null);
  const period: Period = picked ?? prefs?.flashPeriod ?? "yesterday";        // Settings → Default views
  const [ids, setIds] = useState<string[]>([]);
  const q = useQuery({
    queryKey: ["flash", period, ids],
    queryFn: () => api<FlashRes>(`/api/flash?period=${period}&hotelIds=${hotelParam(ids)}`),
  });
  const d = q.data;
  const many = (d?.hotels.length ?? 0) > 1;

  return (
    <>
      <div className="eyebrow">Owners Module · Flash Reports</div>
      <h1>{d?.label ?? "Daily flash"}</h1>
      <div className="row" style={{ marginBottom: 16 }}>
        <HotelPicker value={ids} onChange={setIds} />
        <div className="seg">
          {PERIODS.map(([k, l]) => <button key={k} className={period === k ? "on" : ""} onClick={() => setPeriod(k)}>{l}</button>)}
        </div>
      </div>

      {q.isLoading ? <div className="card muted">Loading…</div> : q.error ? (
        <div className="card error">{(q.error as Error).message}</div>
      ) : d && (
        <>
          {d.total && (
            <div className="tiles strip">
              <Summary label="Occupancy" value={pct(d.total.current.occupancy, { ratio: true })}
                vs={<PtsVar actual={d.total.current.occupancy} base={d.total.lastYear?.occupancy ?? null} />}
                vsBudget={d.total.budget ? <PtsVar actual={d.total.current.occupancy} base={d.total.budget.occupancy} /> : null} />
              <Summary label="ADR" value={money(d.total.current.adr, { decimals: 2 })}
                vs={<MoneyVar actual={d.total.current.adr} base={d.total.lastYear?.adr ?? null} />}
                vsBudget={d.total.budget ? <MoneyVar actual={d.total.current.adr} base={d.total.budget.adr} /> : null} />
              <Summary label="RevPAR" value={money(d.total.current.revpar, { decimals: 2 })}
                vs={<MoneyVar actual={d.total.current.revpar} base={d.total.lastYear?.revpar ?? null} />}
                vsBudget={d.total.budget ? <MoneyVar actual={d.total.current.revpar} base={d.total.budget.revpar} /> : null} />
              <Summary label="Rooms sold" value={num(d.total.current.roomsSold)}
                vs={d.total.lastYear ? <span>{valuePct(d.total.current.roomsSold - d.total.lastYear.roomsSold,
                  d.total.lastYear.roomsSold ? ((d.total.current.roomsSold - d.total.lastYear.roomsSold) / d.total.lastYear.roomsSold) * 100 : null,
                  { signed: true })}</span> : null} />
              <Summary label="Total revenue" value={money(d.total.current.revenue)}
                vs={<MoneyVar actual={d.total.current.revenue} base={d.total.lastYear?.revenue ?? null} />}
                vsBudget={d.total.budget ? <MoneyVar actual={d.total.current.revenue} base={d.total.budget.revenue} /> : null} />
              {/* Demo: profit lines appear on the flash as "monthly only". */}
              <div className="card tile"><div className="eyebrow">GOP · NOI</div><div className="tile-value" style={{ fontSize: 18 }}>Monthly only</div>
                <div className="tile-sub">Reported after month-end close, in Financial Reports</div></div>
            </div>
          )}

          <div className="card">
            <table className="fin">
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th className="num">Occupancy</th><th className="num">vs last year</th>
                  <th className="num">ADR</th><th className="num">vs last year</th>
                  <th className="num">RevPAR</th><th className="num">vs last year</th>
                  <th className="num">Rooms sold</th>
                  <th className="num">Revenue</th><th className="num">vs last year</th>
                </tr>
              </thead>
              <tbody>
                {d.hotels.map((h) => (
                  <tr key={h.id}>
                    <td>
                      {h.name}
                      {h.current && h.daysReported < d.days && (
                        <div className="muted" style={{ fontSize: 11 }}>{num(h.daysReported)} of {num(d.days)} days reported</div>
                      )}
                    </td>
                    {h.current ? (
                      <>
                        <td className="num">{pct(h.current.occupancy, { ratio: true })}</td>
                        <td className="num"><PtsVar actual={h.current.occupancy} base={h.lastYear?.occupancy ?? null} /></td>
                        <td className="num">{money(h.current.adr, { decimals: 2 })}</td>
                        <td className="num"><MoneyVar actual={h.current.adr} base={h.lastYear?.adr ?? null} /></td>
                        <td className="num">{money(h.current.revpar, { decimals: 2 })}</td>
                        <td className="num"><MoneyVar actual={h.current.revpar} base={h.lastYear?.revpar ?? null} /></td>
                        <td className="num">{num(h.current.roomsSold)}</td>
                        <td className="num">{money(h.current.revenue)}</td>
                        <td className="num"><MoneyVar actual={h.current.revenue} base={h.lastYear?.revenue ?? null} /></td>
                      </>
                    ) : (
                      <td colSpan={9} className="muted">
                        No nightly close for this period{h.dataThrough ? ` — latest is ${shortDay(h.dataThrough)}` : ""}.
                      </td>
                    )}
                  </tr>
                ))}
                {many && d.total && (
                  <tr className="total-row">
                    <td>Total · {num(d.total.hotels)} hotels</td>
                    <td className="num">{pct(d.total.current.occupancy, { ratio: true })}</td>
                    <td className="num"><PtsVar actual={d.total.current.occupancy} base={d.total.lastYear?.occupancy ?? null} /></td>
                    <td className="num">{money(d.total.current.adr, { decimals: 2 })}</td>
                    <td className="num"><MoneyVar actual={d.total.current.adr} base={d.total.lastYear?.adr ?? null} /></td>
                    <td className="num">{money(d.total.current.revpar, { decimals: 2 })}</td>
                    <td className="num"><MoneyVar actual={d.total.current.revpar} base={d.total.lastYear?.revpar ?? null} /></td>
                    <td className="num">{num(d.total.current.roomsSold)}</td>
                    <td className="num">{money(d.total.current.revenue)}</td>
                    <td className="num"><MoneyVar actual={d.total.current.revenue} base={d.total.lastYear?.revenue ?? null} /></td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
              From each hotel's nightly close. Last year compares the same days of the week (364 days earlier).
              Budget is each hotel's monthly budget spread evenly over the days it reported{d.total && !d.total.budget ? " (shown only when every hotel in view has a budget)" : ""}.
              Revenue is rooms, food & beverage and other operated revenue; profit appears in Financial Reports after month end.
            </p>
          </div>
        </>
      )}
    </>
  );
}

function Summary({ label, value, vs, vsBudget }: { label: string; value: string; vs: ReactNode; vsBudget?: ReactNode }) {
  return (
    <div className="card tile">
      <div className="eyebrow">{label}</div>
      <div className="tile-value">{value}</div>
      {vs && <div className="tile-sub">{vs} vs last year</div>}
      {vsBudget && <div className="tile-sub">{vsBudget} vs budget</div>}
    </div>
  );
}

const shortDay = (iso: string) => new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
